import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: number;
  message: string;
  type: NotificationType;
  duration: number;
}

let _nextId = 1;

@Injectable({ providedIn: 'root' })
export class NotificationService {
  /** Stack of active toasts. New ones are appended; each auto-removes after its duration. */
  readonly notifications = signal<Notification[]>([]);

  // ── Public helpers ──────────────────────────────────────────────────────────

  success(message: string, duration = 5000): void {
    this._add(message, 'success', duration);
  }

  error(message: string, duration = 6000): void {
    this._add(message, 'error', duration);
  }

  warning(message: string, duration = 5500): void {
    this._add(message, 'warning', duration);
  }

  info(message: string, duration = 5000): void {
    this._add(message, 'info', duration);
  }

  /**
   * Generic entry-point kept for backwards-compatibility with existing callers
   * that pass a type string as the second argument.
   */
  show(message: string, type: NotificationType = 'success', duration?: number): void {
    const defaults: Record<NotificationType, number> = {
      success: 5000, error: 6000, warning: 5500, info: 5000,
    };
    this._add(message, type, duration ?? defaults[type]);
  }

  dismiss(id: number): void {
    this.notifications.update(list => list.filter(n => n.id !== id));
  }

  // ── Internal ────────────────────────────────────────────────────────────────

  private _add(message: string, type: NotificationType, duration: number): void {
    const id = _nextId++;
    this.notifications.update(list => [...list, { id, message, type, duration }]);
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }
  }
}
